import type { UserRetrieveResponse, BffCrmFeatureAccessRetrieveResponse } from '@fireflyhealth/api'
import type { ScheduleEventColors, Tones } from '~/shared/lib/theme'

/**
 * Non-null narrowing of `UserRetrieveResponse['person']`. The generated type
 * marks `person` as optional because provider users have no `Person`; every
 * other consumer wants the non-null shape, so this alias is the canonical one.
 */
export type Person = NonNullable<UserRetrieveResponse['person']>

/**
 * Patient-narrowed view of `UserRetrieveResponse`. `person` is `.optional()` in
 * the generated response because `/user/{id}/` serves both patients and
 * providers, but every patient profile is created with a `Person` record
 * server-side, so patient-detail screens treat it as required. Production
 * code reaches this shape via `getPatient`, which validates `is_patient`
 * before storing the user; the test-only `assertPatientUser` in
 * `~/vitest/assertions` bridges generated mock factories that return the
 * looser `UserRetrieveResponse` shape.
 */
export type PatientUser = Omit<UserRetrieveResponse, 'person'> & {
  person: Person
}

/**
 * The production counterpart to `assertPatientUser`: a screen that needs `person` narrows with
 * this instead of asserting, so a user read from the store before its person arrived renders
 * nothing rather than throwing on the way in.
 */
export const isPatientUser = (user: UserRetrieveResponse | undefined): user is PatientUser =>
  user?.person != null

// Provider feature-access map, sourced from the generated CRM BFF client
// (GET /bff/crm/feature-access/) so the feature names stay in sync with the
// back-end. Names are snake_case, matching the auto-generated client.
export type FeatureAccess = BffCrmFeatureAccessRetrieveResponse

export type Features = keyof FeatureAccess['tenant']

declare module 'react' {
  type UseState<S> = [S, Dispatch<SetStateAction<S>>]
}

// Following https://mui.com/material-ui/customization/theming/#typescript to type
// our own theme customizations. Augmented on `@mui/material/styles` rather than the
// `styles/createPalette` and `styles/createTypography` submodules: v7 stopped
// publishing those as addressable modules, so augmenting them silently detached
// every custom key (`tones`, `brand`, `fontFamilyMono`, …) from the theme types.
declare module '@mui/material/styles' {
  // The Firefly ramps in `~/shared/lib/theme` carry MUI's tonal keys (`50`…`900`, and `A50`/`A100`
  // on grey) alongside main/light/dark, and components index them directly. Optional because
  // the smaller ramps (blueGrey, brown) define only the named stops.
  interface PaletteColor extends Partial<import('@mui/material').Color> {
    lighter: string
    darker: string
  }
  interface SimplePaletteColorOptions {
    lighter?: string
    darker?: string
  }

  interface Palette {
    brand: PaletteColor
    navy: PaletteColor
    blueGrey: PaletteColor
    brown: PaletteColor
    icons: {
      successGreen: string
      errorRed: string
    }
    tones: Tones
    schedule: ScheduleEventColors
  }
  interface PaletteOptions {
    brand: import('@mui/material/styles').PaletteColorOptions
    navy: import('@mui/material/styles').PaletteColorOptions
    blueGrey: import('@mui/material/styles').PaletteColorOptions
    brown: import('@mui/material/styles').PaletteColorOptions
    icons?: {
      successGreen: string
      errorRed: string
    }
    tones: Tones
    schedule: ScheduleEventColors
  }

  interface TypeBackground {
    highlight: string
    external: string
    page: string
    pane: string
    cardFooter: string
  }

  // Both themes carry a monospace stack for ID/code values (see DetailGrid's
  // `mono` items).
  interface TypographyVariants {
    fontFamilyMono: string
  }
  interface TypographyVariantsOptions {
    fontFamilyMono?: string
  }
}

export type ValueSetter = (
  name: string,
  value?: string | boolean | null | number,
) => {
  name: string
  onChange: (e: React.ChangeEvent) => void
  disabled: boolean
  value?: string | boolean | null | number
}

/** The insurance coverage window a member's profile header reads. */
export interface IPatientInsuranceDetails {
  coverageStartDate: string | null
  coverageEndDate: string | null
  insurancePayerName: string | null
  insurancePayerShortName?: string | null
}
